import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject
} from '@apollo/client';
import initApollo from '../utils/init-Apollo';
import CONFIG from '../config';
import { getLocaleFromBrowser } from '../utils/helpers';
import { ApolloOptions } from '../utils/helpers/ssrContext';
import { Language } from '../utils/types';

interface PageProps {
  apolloState?: NormalizedCacheObject;
  apolloOptions?: ApolloOptions;
  currentLang?: Language;
}

interface ApolloWrapperProps {
  children: React.ReactNode;
  pageProps: PageProps;
}

export const ApolloWrapper = ({ children, pageProps }: ApolloWrapperProps) => {
  const lastApolloState = useRef(pageProps?.apolloState);

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const apolloOptions = useMemo((): ApolloOptions => {
    if (pageProps?.apolloOptions) return pageProps.apolloOptions;
    // 1. On the server OR during the initial hydration on the client (while isMounted === false),
    // we strictly use either the provided language or the default config (az).
    // This guarantees a 100% match of the HTML text during the initial React tree construction.
    if (!isMounted) {
      return {
        lang: pageProps?.currentLang || (CONFIG.defaultLocale as Language)
      };
    }

    // 2. Once the component has mounted in the browser, we can safely
    // read the browser locale. Hydration has already completed successfully by this point.
    const lang = (pageProps?.currentLang ||
      getLocaleFromBrowser() ||
      CONFIG.defaultLocale) as Language;

    return { lang };
  }, [pageProps?.apolloOptions, pageProps?.currentLang, isMounted]);

  const apolloClient = useMemo(
    () =>
      initApollo(
        pageProps?.apolloState,
        apolloOptions
      ) as ApolloClient<NormalizedCacheObject>,
    [pageProps?.apolloState, apolloOptions]
  );

  // Merge SSG/SSR data into cache when navigating between pages.
  // On popstate (back/forward) navigation skip the restore entirely so that
  // fetchMore data accumulated during the previous visit is preserved.
  // On forward navigation merge instead of replace so client-side
  // paginated data is not wiped by a partial SSR apolloState.
  useEffect(() => {
    const apolloState = pageProps?.apolloState;

    if (apolloState && apolloState !== lastApolloState.current) {
      const isPopState =
        typeof window !== 'undefined' &&
        (window as Window & { isPopState?: boolean }).isPopState;

      if (!isPopState) {
        const existingCache = apolloClient.cache.extract();

        apolloClient.cache.restore({
          ...existingCache,
          ...apolloState,
          ROOT_QUERY: {
            ...existingCache?.ROOT_QUERY,
            ...apolloState?.ROOT_QUERY
          }
        });
      }
      lastApolloState.current = apolloState;
    }
  }, [pageProps?.apolloState, apolloClient]);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};
